/**
 * Bloqueig de còpia i enganxada a les pantalles amb contingut del cas.
 *
 * **El problema que resol.** Res no impedia que un alumne seleccionés l'enunciat
 * del cas i les evidències, ho enganxés a una IA generativa i tornés a enganxar
 * la resposta als camps d'anàlisi. L'exercici avalua el raonament analític; si
 * el text viatja amunt i avall amb dues tecles, no avalua res.
 *
 * **Qui queda bloquejat.** Només els comptes de rol `analyst`. Això inclou les
 * dues menes d'alumne —els grups que entren amb codi imprès (`is_exam_account`)
 * i els analistes amb compte propi—, perquè totes dues són `analyst` a la base
 * de dades. Instructors i administradors no es toquen: han de poder copiar per
 * corregir, preparar material i donar suport.
 *
 * La comprovació que fa el qui crida ha de ser **positiva** (`role === 'analyst'`)
 * i no la negació de «és instructor». Amb la negació, un `user` encara nul
 * activaria el bloqueig, i el cas dolent seria bloquejar un instructor.
 *
 * **Fins on arriba, i fins on no.** Això atura el camí fàcil: el menú del botó
 * dret, Ctrl+C/Ctrl+V i arrossegar la selecció a fora. No atura qui vulgui
 * fer-ho de debò —el text del cas viatja a la resposta de l'API, les eines de
 * desenvolupament del navegador hi són igualment, i una foto amb el mòbil se
 * salta qualsevol cosa que puguem escriure aquí—. És un dissuasiu, no un
 * control de seguretat; no en construïm cap defensa que doni per fet el
 * contrari.
 *
 * **Per què no bloquegem F12 ni Ctrl+U.** Perseguir les eines de
 * desenvolupament no funciona (el menú del navegador hi arriba igual) i fa mal:
 * deixa la pàgina barallant-se amb qui la fa servir. Bloquegem el camí fàcil i
 * prou.
 *
 * Nota sobre les tecles: no cal escoltar `keydown`. Ctrl+C, Ctrl+X i Ctrl+V
 * disparen els esdeveniments `copy`, `cut` i `paste`, els mateixos que el menú
 * del botó dret. Bloquejant els esdeveniments es tanquen les dues portes alhora.
 *
 * Retorna si cal ensenyar l'avís, perquè la pantalla pinti <AvisCopia />. El
 * temporitzador viu aquí i no a cada pàgina: és la mateixa mesura a totes dues
 * i no ha de divergir.
 */

import { useEffect, useState } from 'react';

const ESDEVENIMENTS = ['contextmenu', 'copy', 'cut', 'paste', 'dragstart'];

const DURADA_AVIS_MS = 2500;

export function useProteccioCopia(actiu) {
  const [avis, setAvis] = useState(false);

  useEffect(() => {
    if (!actiu) return undefined;

    const bloqueja = (event) => {
      event.preventDefault();
      setAvis(true);
    };

    // En fase de captura: així el bloqueig arriba abans que qualsevol handler
    // de component i no depèn que ningú deixi propagar l'esdeveniment.
    ESDEVENIMENTS.forEach((nom) => document.addEventListener(nom, bloqueja, true));
    document.body.classList.add('sense-copia');

    return () => {
      ESDEVENIMENTS.forEach((nom) => document.removeEventListener(nom, bloqueja, true));
      document.body.classList.remove('sense-copia');
    };
  }, [actiu]);

  // L'avís s'amaga sol: és un recordatori, no una cosa que s'hagi de clicar
  // enmig d'un examen amb el rellotge en marxa. Mentre és visible, tornar a
  // provar-ho no reinicia el compte enrere —React no re-renderitza si l'estat
  // no canvia—, i això ja fa de fre: prement Ctrl+C sense parar no surt una
  // pluja de missatges.
  useEffect(() => {
    if (!avis) return undefined;
    const temporitzador = setTimeout(() => setAvis(false), DURADA_AVIS_MS);
    return () => clearTimeout(temporitzador);
  }, [avis]);

  return avis;
}
